import { tiltify } from "@playlive/tiltify-core";
import { useState } from "react";
import useTiltifyConfig from "@/hooks/useTiltifyConfig";

/**
 * Dev-only helper for the Developer Settings modal.
 *
 * Lets a developer act as any Tiltify user locally by supplying their public
 * user slug (the last path segment of their `tiltify.com/@slug` URL). We look
 * up the user through the Tiltify proxy — no OAuth token is required because
 * the proxy handles auth server-side, and every campaign/poll fetch in the app
 * already routes through it.
 *
 * When submitted successfully, we populate:
 *   - tiltifyUserID
 *   - tiltifyUsername
 *   - tiltifyUserSlug
 *   - tiltifyDevImpersonation = true  (bypasses the token-refresh flow in TiltifySetup)
 */
export default function DevTiltifyImpersonate() {
  const { config, setField, clearField } = useTiltifyConfig();
  const [slug, setSlug] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isImpersonating = config.tiltifyDevImpersonation === true;

  async function handleImpersonate() {
    const cleaned = slug.trim().replace(/^@/, "");
    if (!cleaned) {
      setError("Enter a Tiltify user slug (e.g. `@johndoe` → `johndoe`).");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const proxyUrl = import.meta.env.VITE_TILTIFY_PROXY_URL;
      if (!proxyUrl) {
        throw new Error("VITE_TILTIFY_PROXY_URL is not set — cannot look up users.");
      }
      tiltify.UseProxy(proxyUrl);

      const user = await tiltify.FindUser(cleaned);
      if (!user) {
        throw new Error(`No Tiltify user found with slug "${cleaned}".`);
      }

      setField("tiltifyUserID", user.id);
      setField("tiltifyUsername", user.username);
      setField("tiltifyUserSlug", user.slug);
      setField("tiltifyDevImpersonation", true);

      // Clear any stale campaign/poll state from a previous session so campaign
      // pickers refetch for the newly-impersonated user.
      clearField("tiltifyCampaignID");
      clearField("tiltifyCampaignSlug");
      clearField("tiltifyCampaignName");
      clearField("tiltifyCampaignGoal");
      clearField("tiltifyPollID");
      clearField("tiltifyAccessToken");
      clearField("tiltifyRefreshToken");
      clearField("tiltifyTokenExpiresIn");
      clearField("tiltifyTokenCreatedAt");

      setSlug("");
    } catch (err) {
      setError((err as Error).message ?? "Failed to look up Tiltify user.");
    } finally {
      setIsLoading(false);
    }
  }

  function handleStopImpersonating() {
    clearField("tiltifyDevImpersonation");
    clearField("tiltifyUserID");
    clearField("tiltifyUsername");
    clearField("tiltifyUserSlug");
    clearField("tiltifyCampaignID");
    clearField("tiltifyCampaignSlug");
    clearField("tiltifyCampaignName");
    clearField("tiltifyCampaignGoal");
    clearField("tiltifyPollID");
  }

  return (
    <div className="border border-base-300 rounded-md p-3 mb-3">
      <h4 className="text-sm font-semibold mb-1">Impersonate Tiltify user</h4>
      <p className="text-xs opacity-70 mb-2">
        Dev-only. Fetches the user's campaigns through the proxy without OAuth.
      </p>

      {isImpersonating ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm">
            Currently impersonating <b>{config.tiltifyUsername || "(unknown)"}</b>
            {config.tiltifyUserSlug ? (
              <span className="opacity-70"> ({config.tiltifyUserSlug})</span>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={handleStopImpersonating}
          >
            Stop
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            spellCheck={false}
            className="input input-sm input-bordered flex-1"
            placeholder="tiltify user slug (e.g. johndoe)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleImpersonate();
              }
            }}
            disabled={isLoading}
          />
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={handleImpersonate}
            disabled={isLoading || slug.trim().length === 0}
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              "Sign in as user"
            )}
          </button>
        </div>
      )}

      {error && <div className="text-xs text-error mt-2">{error}</div>}
    </div>
  );
}
